import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { api, type RepCommissionMonth, type SalesRepDetail, type SalesStageBucket, type SalesDealRow } from '../lib/api';
import {
  ActionButton, Card, CardHeader, MetricCard, Table, Th, Tr, Td, Badge, Spinner, EmptyState, Select,
} from '../components/ui';
import { ChartCard } from '../components/charts';
import { fmtCurrencyWhole, fmtPct, fmtMonth } from '../lib/fmt';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
  ComposedChart,
} from 'recharts';

const hdr: React.CSSProperties = { fontSize: 13, fontWeight: 600 };
const c = (n: number | null | undefined) => (n == null ? '—' : fmtCurrencyWhole(n));
const p = (n: number | null | undefined) => (n == null ? '—' : fmtPct(n));
const tile = { display: 'flex', gap: 8, padding: '10px 16px', flexWrap: 'wrap' as const };

const MONTH_OPTIONS = [3, 6, 12, 24];
const AXIS = 'var(--color-text-muted)';
const GRID = 'var(--color-border)';

function repLabel(r: { owner_name: string | null; email: string | null; hubspot_owner_id: string }): string {
  return r.owner_name ?? r.email ?? r.hubspot_owner_id;
}

function MonthTrend({ data }: { data: Array<{ month: string; closed_won: number; target: number | null }> }) {
  const hasTarget = data.some((d) => d.target != null);
  return (
    <ChartCard title="Closed-won by month" sub={hasTarget ? 'bars vs. target line' : 'trailing window'}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: AXIS }} />
          <YAxis tickFormatter={(v) => fmtCurrencyWhole(v)} tick={{ fontSize: 10, fill: AXIS }} width={64} />
          <Tooltip formatter={(v: number) => fmtCurrencyWhole(v)} labelFormatter={fmtMonth} />
          {hasTarget && <Legend wrapperStyle={{ fontSize: 11 }} />}
          <Bar dataKey="closed_won" name="Closed won" fill="var(--color-green)" radius={[3, 3, 0, 0]} />
          {hasTarget && (
            <Line type="monotone" dataKey="target" name="Target" stroke="var(--color-amber)" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// Attainment as a coloured metric tile. Green at/above plan, amber below.
// Always fiscal-year-to-date, independent of the lookback selector.
function AttainmentTile({ quota, period }: { quota: { target: number; actual: number; attainment: number | null }; period: string }) {
  const a = quota.attainment;
  return (
    <MetricCard
      label={`Quota attainment · ${period}`}
      value={a == null ? '—' : fmtPct(a)}
      sub={`${fmtCurrencyWhole(quota.actual)} of ${fmtCurrencyWhole(quota.target)} to date`}
      accent={a != null && a >= 1 ? 'green' : 'amber'}
    />
  );
}

function DemandCurve({ data }: { data: Array<{ month: string; expected: number; gross: number }> }) {
  if (!data.length) return null;
  return (
    <ChartCard title="Forward demand" sub="open deals × stage probability, by close month">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: AXIS }} />
          <YAxis tickFormatter={(v) => fmtCurrencyWhole(v)} tick={{ fontSize: 10, fill: AXIS }} width={64} />
          <Tooltip formatter={(v: number) => fmtCurrencyWhole(v)} labelFormatter={fmtMonth} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="expected" name="Weighted" stroke="var(--color-blue)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="gross" name="Gross" stroke="var(--color-text-muted)" strokeWidth={1} strokeDasharray="4 3" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function PipelineByStage({ stages }: { stages: SalesStageBucket[] }) {
  const max = Math.max(1, ...stages.map((s) => s.weighted));
  return (
    <Card>
      <CardHeader><div style={hdr}>Open pipeline by stage</div></CardHeader>
      {stages.length === 0 ? <EmptyState message="No open deals." /> : (
        <Table>
          <thead><tr><Th>Stage</Th><Th right>Deals</Th><Th right>Weighted</Th><Th right>Gross</Th></tr></thead>
          <tbody>
            {stages.map((s) => (
              <Tr key={s.stage_id ?? s.label ?? 'none'}>
                <Td>
                  <div>{s.label ?? '(unmapped)'}</div>
                  <div style={{ height: 4, marginTop: 3, background: 'var(--color-border)', borderRadius: 2 }}>
                    <div style={{ height: 4, width: `${(s.weighted / max) * 100}%`, background: 'var(--color-blue)', borderRadius: 2 }} />
                  </div>
                </Td>
                <Td right>{s.count}</Td>
                <Td right>{c(s.weighted)}</Td>
                <Td right>{c(s.amount)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function DealTable({ title, rows, dateLabel }: { title: string; rows: SalesDealRow[]; dateLabel: string }) {
  return (
    <Card>
      <CardHeader><div style={hdr}>{title}</div></CardHeader>
      {rows.length === 0 ? <EmptyState message="None in window." /> : (
        <Table>
          <thead><tr><Th>Deal</Th><Th>Stage</Th><Th>{dateLabel}</Th><Th right>Amount</Th><Th right>Weighted</Th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <Tr key={d.deal_id}>
                <Td bold>{d.name ?? d.deal_id}</Td>
                <Td muted>{d.stage_label ?? '—'}</Td>
                <Td muted>{d.close_date ?? '—'}</Td>
                <Td right>{c(d.amount)}</Td>
                <Td right>{c(d.weighted)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function RepDetail({ rep, period }: { rep: SalesRepDetail; period: string }) {
  const k = rep.kpis;
  return (
    <Card>
      <CardHeader>
        <div style={hdr}>
          {repLabel(rep)} {rep.archived && <Badge variant="gray">former</Badge>}
        </div>
      </CardHeader>
      <div style={tile}>
        {rep.quota && <AttainmentTile quota={rep.quota} period={period} />}
        <MetricCard label="Bookings / mo" value={c(k.bookings_per_month)} sub="recency-weighted run-rate" accent="blue" />
        <MetricCard label="Win rate" value={p(k.win_rate)} sub={`${k.won_count}/${k.closed_count} closed`} />
        <MetricCard label="Avg deal" value={c(k.avg_deal_size)} sub={k.sales_cycle_days == null ? undefined : `${k.sales_cycle_days}d cycle`} />
        <MetricCard label="Open pipeline" value={c(k.open_pipeline_weighted)} sub="weighted" accent="green" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, padding: '0 16px 12px' }}>
        <MonthTrend data={rep.trend} />
        <PipelineByStage stages={rep.pipeline_by_stage} />
        <DealTable title="Recent closed-won" rows={rep.recent_won} dateLabel="Closed" />
        <DealTable title="Top open deals" rows={rep.open_deals} dateLabel="Close" />
      </div>
    </Card>
  );
}

// ─── My commissions ──────────────────────────────────────────────────────────
// A rep's own payouts, by billed month. Deliberately available to any rep
// without the report_commissions permission — that key gates the company-wide
// payment run; seeing your own commission is part of being a rep.

function commissionStatusBadge(status: RepCommissionMonth['status']) {
  switch (status) {
    case 'paid':     return <Badge variant="green">Paid</Badge>;
    case 'approved': return <Badge variant="blue">Approved</Badge>;
    case 'locked':   return <Badge variant="locked">Locked</Badge>;
    default:         return <Badge variant="amber">Open</Badge>;
  }
}

function MyCommissions({ months, rep }: { months: number; rep?: string }) {
  const { data, isLoading, error } = useQuery(() => api.sales.commissions(months, rep), [months, rep]);
  const [showRows, setShowRows] = useState(false);

  if (isLoading) return <Card><CardHeader><div style={hdr}>Commissions</div></CardHeader><Spinner /></Card>;
  if (error || !data) return <Card><CardHeader><div style={hdr}>Commissions</div></CardHeader><EmptyState message={error ?? 'No data'} /></Card>;

  const title = data.is_self ? 'My commissions' : 'Commissions';

  if (!data.hubspot_owner_id) {
    return (
      <Card>
        <CardHeader><div style={hdr}>{title}</div></CardHeader>
        <EmptyState message="Your account isn’t linked to a HubSpot deal owner, so there are no commissions to show." />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div style={hdr}>{title}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            Paid <strong style={{ color: 'var(--color-text)' }}>{fmtCurrencyWhole(data.total_paid)}</strong>
            {' · '}
            Pending <strong style={{ color: 'var(--color-text)' }}>{fmtCurrencyWhole(data.total_pending)}</strong>
          </span>
          <ActionButton onClick={() => setShowRows((v) => !v)}>
            {showRows ? 'Hide detail' : 'Show detail'}
          </ActionButton>
        </div>
      </CardHeader>

      {data.months.length === 0 ? (
        <EmptyState message="No commission earned yet. Commission is paid on invoices raised against your closed-won deals." />
      ) : (
        <Table>
          <thead><tr>
            <Th>Period</Th>
            <Th right>Invoiced services</Th>
            <Th right>Commission</Th>
            <Th>Status</Th>
          </tr></thead>
          <tbody>
            {[...data.months].reverse().map((m) => (
              <Tr key={m.period_month}>
                <Td bold>{fmtMonth(m.period_month)}</Td>
                <Td right>{fmtCurrencyWhole(m.commissionable_amount)}</Td>
                <Td right bold>{fmtCurrencyWhole(m.commission_amount)}</Td>
                <Td>{commissionStatusBadge(m.status)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {showRows && data.recent.length > 0 && (
        <div style={{ overflowX: 'auto', borderTop: '0.5px solid var(--color-border)' }}>
          <Table>
            <thead><tr>
              <Th>Account</Th><Th>Invoice</Th><Th right>Billed</Th>
              <Th right>Invoiced services</Th><Th right>Split</Th><Th right>Rate</Th><Th right>Commission</Th>
            </tr></thead>
            <tbody>
              {data.recent.map((r) => (
                <Tr key={`${r.chargeover_invoice_id}-${r.hubspot_owner_id}`}>
                  <Td bold>{r.account_name ?? r.tempo_account_id ?? '—'}</Td>
                  <Td>{r.invoice_number ?? r.chargeover_invoice_id}</Td>
                  <Td right>{r.billed_date}</Td>
                  <Td right>{fmtCurrencyWhole(r.commissionable_amount)}</Td>
                  <Td right>{(r.split_percentage * 100).toFixed(r.split_percentage * 100 % 1 === 0 ? 0 : 2)}%</Td>
                  <Td right>{r.rate_pct}%</Td>
                  <Td right bold>{fmtCurrencyWhole(r.commission_amount)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

export default function SalesDashboardPage() {
  const [months, setMonths] = useState(12);
  const [rep, setRep] = useState<string | undefined>(undefined);
  const { data, isLoading, error } = useQuery(() => api.sales.performance(months, rep), [months, rep]);

  if (isLoading) return <Card><CardHeader><div style={hdr}>Sales performance</div></CardHeader><Spinner /></Card>;
  if (error || !data) return <Card><CardHeader><div style={hdr}>Sales performance</div></CardHeader><EmptyState message={error ?? 'No data'} /></Card>;

  const t = data.team.kpis;
  // The rep the dropdown reflects: explicit selection, else the server's default
  // (self), else none. Kept in sync with `rep` so every pick is a real change.
  const selectedOwnerId = rep ?? data.rep?.hubspot_owner_id ?? '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Lookback</div>
        <Select value={String(months)} onChange={(v) => setMonths(Number(v))}>
          {MONTH_OPTIONS.map((m) => <option key={m} value={m}>{m} months</option>)}
        </Select>
        {data.is_manager && data.reps_directory && (
          <>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 8 }}>Rep</div>
            <Select value={selectedOwnerId} onChange={(v) => setRep(v || undefined)}>
              {/* Placeholder so the controlled value always matches an option —
                  otherwise picking the first rep fires no change event. */}
              {!selectedOwnerId && <option value="">Select a rep…</option>}
              {data.reps_directory.map((r) => (
                <option key={r.hubspot_owner_id} value={r.hubspot_owner_id}>
                  {(r.owner_name ?? r.hubspot_owner_id) + (r.archived ? ' (former)' : '')}
                </option>
              ))}
            </Select>
          </>
        )}
        {data.is_manager && (
          <Link to="/sales/quotas" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-blue)' }}>Set targets →</Link>
        )}
      </div>

      {/* Team overview */}
      <Card>
        <CardHeader>
          <div style={hdr}>Team overview</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{fmtMonth(data.window.from)} – {fmtMonth(data.window.to)}</div>
        </CardHeader>
        <div style={tile}>
          {data.team.quota && <AttainmentTile quota={data.team.quota} period={data.fiscal_year.label} />}
          <MetricCard label="Active reps" value={t.active_reps} sub={`${t.productive_reps} with wins`} />
          <MetricCard label="Bookings / mo" value={c(t.total_bookings_per_month)} sub={t.avg_bookings_per_rep == null ? undefined : `${c(t.avg_bookings_per_rep)} / rep`} accent="blue" />
          <MetricCard label="Win rate" value={p(t.avg_win_rate)} sub={t.avg_deal_size == null ? undefined : `avg deal ${c(t.avg_deal_size)}`} />
          <MetricCard label="Open pipeline" value={c(t.open_pipeline_weighted)} sub={t.avg_sales_cycle_days == null ? undefined : `${t.avg_sales_cycle_days}d cycle`} accent="green" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, padding: '0 16px 12px' }}>
          <MonthTrend data={data.team.trend} />
          <DemandCurve data={data.team.expected_bookings_by_month} />
          <PipelineByStage stages={data.team.pipeline_by_stage} />
        </div>
      </Card>

      {/* Leaderboard — managers only */}
      {data.is_manager && data.leaderboard && (
        <Card>
          <CardHeader><div style={hdr}>Rep leaderboard</div></CardHeader>
          {data.leaderboard.length === 0 ? <EmptyState message="No reps with deals in window." /> : (
            <Table>
              <thead><tr>
                <Th>Rep</Th><Th right>Bookings / mo</Th><Th right>Won</Th><Th right>Win rate</Th>
                <Th right>Avg deal</Th><Th right>Cycle (d)</Th><Th right>Open pipeline</Th>
              </tr></thead>
              <tbody>
                {data.leaderboard.map((r) => (
                  <Tr key={r.hubspot_owner_id} onClick={() => setRep(r.hubspot_owner_id)} selected={r.hubspot_owner_id === data.rep?.hubspot_owner_id}>
                    <Td bold>{repLabel(r)} {r.archived && <Badge variant="gray">former</Badge>}</Td>
                    <Td right>{c(r.bookings_per_month)}</Td>
                    <Td right>{r.won_count}</Td>
                    <Td right>{p(r.win_rate)}</Td>
                    <Td right>{c(r.avg_deal_size)}</Td>
                    <Td right>{r.sales_cycle_days ?? '—'}</Td>
                    <Td right>{c(r.open_pipeline_weighted)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* Commissions — what the rep actually gets paid, as distinct from what
          they booked. Keyed to the billed month, not the deal close month. */}
      <MyCommissions months={months} rep={rep} />

      {/* Individual */}
      {data.rep ? <RepDetail rep={data.rep} period={data.fiscal_year.label} /> : (
        <Card>
          <CardHeader><div style={hdr}>Individual</div></CardHeader>
          <EmptyState message={data.is_manager ? 'Select a rep above to see their detail.' : 'Your account isn’t linked to a HubSpot deal owner, so there’s no individual view yet.'} />
        </Card>
      )}
    </div>
  );
}
